// Ported ~verbatim from nenda-autoplayer's src/components/Playback/Video/HtmlVideoPlayer.tsx.
// Already plain <video>/<div> upstream — zero React Native imports. Only change: the unused
// videoPerformanceLogger import (dead/commented-out upstream) was dropped, and `__DEV__` (RN
// global) became `import.meta.env.DEV` (Vite) — see devLogger.ts for the same substitution.
import { PlaybackErrorLevel } from "../types/error";
import React, { useRef, useEffect, useCallback } from "react";
import { useStallLogger } from "./useStallLogger";

const HtmlVideoPlayer: React.FC<{
  src: string;
  isSoundEnabled?: boolean;
  startTime?: number;
  onPlaybackComplete: () => void;
  onError?: (error: Error, level: PlaybackErrorLevel) => void;
  onEvent?: (event: string) => void;
  loop?: boolean;
  /**
   * ADDITIVE, default `true` (every existing call site is unaffected). See
   * `ShakaPlayer.tsx`'s own doc comment on the same prop - identical
   * contract: `false` primes `src`/`currentTime` without calling `.play()`,
   * for Foyer's pre-roll layer pair (port plan §6b); flip it later on the
   * SAME instance (same `key`) to hand over without losing what was already
   * buffered.
   */
  active?: boolean;
  /** ADDITIVE - same contract as `ShakaPlayer.tsx`'s prop of the same name:
   *  fires once, on the first real `playing` event. */
  onPlaybackStarted?: () => void;
}> = ({
  src,
  isSoundEnabled = false,
  loop = false,
  startTime = 0,
  onPlaybackComplete,
  onError,
  onEvent,
  active = true,
  onPlaybackStarted,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hasStartedRef = useRef(false);
  const handlePlayingOnce = () => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    onPlaybackStarted?.();
  };
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(true);

  const handleEnded = () => {
    setIsPlaying(false);
    if (onPlaybackComplete) {
      onPlaybackComplete();
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handleError = (
    event: React.SyntheticEvent<HTMLVideoElement, Event>
  ) => {
    const error = event.currentTarget.error;
    if (error) {
      const errorMessage = `Video playback error: ${error.message}`;
      console.error(errorMessage);
      onError?.(new Error(errorMessage), PlaybackErrorLevel.FAILURE);
      setIsPlaying(false);
    }
  };

  const handleStallLog = useCallback((msg: string) => {
    if (import.meta.env.DEV) console.log("[StallLogger]", msg);
  }, []);

  const handleLongStall = useCallback(() => {
    onError?.(
      new Error("HTML Playback long stall"),
      PlaybackErrorLevel.WARNING
    );
  }, []);

  const handleStallRecovery = useCallback((stallDuration: number) => {
    if (stallDuration > 0) {
      onEvent?.(`HTML playback recovered after ${stallDuration} ms`);
    }
  }, []);

  useStallLogger({
    videoRef,
    log: handleStallLog,
    onLongStall: handleLongStall,
    onStallRecovered: handleStallRecovery,
    longStallThresholdMs: 3000,
  });

  // Primes `src`/`currentTime` regardless of `active` - a paused element with
  // its currentTime already seeked and bytes already downloading IS the
  // pre-roll, whether or not `.play()` is ever called on it. `.play()` itself
  // is centralized in the effect below, same split as ShakaPlayer.tsx's own
  // `active` prop, for the same reason: flipping `active` alone must never
  // re-run this (no reseek, no re-buffer from scratch).
  useEffect(() => {
    const handlePlaying = () => {
      setIsVisible(true);
    };

    const video = videoRef.current;
    if (!video) return;
    setIsVisible(false);
    video.addEventListener("playing", handlePlaying);
    video.currentTime = startTime;

    return () => {
      video.removeEventListener("playing", handlePlaying);
    };
  }, [startTime]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (active) {
      if (!isPlaying) {
        const playPromise = video.play();
        // play() returns a promise that can reject (autoplay policy / decode);
        // catch it so it never becomes an unhandled rejection.
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch((e: unknown) => {
            onError?.(
              new Error(
                `HTML video play() rejected: ${e instanceof Error ? e.message : e}`
              ),
              PlaybackErrorLevel.WARNING
            );
          });
        }
      }
    } else {
      video.pause();
    }
  }, [active, isPlaying]);

  return (
    <>
      {!isVisible && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "black",
            height: "100%",
            width: "100%",
            zIndex: 99,
          }}
        />
      )}

      <video
        ref={videoRef}
        src={src}
        loop={loop}
        controls={false}
        autoPlay={active}
        muted={!isSoundEnabled}
        playsInline
        style={{ width: "100%", height: "100%" }}
        onEnded={handleEnded}
        onPlay={handlePlay}
        onPlaying={handlePlayingOnce}
        onError={handleError}
      />
    </>
  );
};

export default HtmlVideoPlayer;
